import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const TaskView = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/instructional/tasks/${taskId}`);
      setTask(res.data.task);
      setCompleted(res.data.task.completed);
    } catch (err) {
      console.error('Fetch task error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/api/instructional/tasks/${taskId}/complete`);
      setCompleted(true);
    } catch (err) {
      console.error('Complete error:', err);
    } finally {
      setCompleting(false);
    }
  };

  const taskTypeLabel = (type) => {
    const types = {
      lecture: '🎥 Lecture',
      workbook: '📓 Workbook',
      discussion: '💬 Discussion Board',
      live_discussion: '🎙️ Live Discussion',
      live_practice: '🤝 Live Practice',
      quiz: '📝 Quiz'
    };
    return types[type] || type;
  };

  const renderBlock = (block, index) => {
    if (block.type === 'heading') {
      return <h2 key={index} style={styles.contentHeading}>{block.content}</h2>;
    }
    if (block.type === 'text') {
      return <p key={index} style={styles.contentText}>{block.content}</p>;
    }
    if (block.type === 'video_embed') {
      return (
        <div key={index} style={styles.videoContainer}>
          <iframe src={block.url} style={styles.video} allowFullScreen title={`video-${index}`} />
        </div>
      );
    }
    if (block.type === 'video_upload') {
      return (
        <div key={index} style={styles.videoContainer}>
          <video src={block.url} controls style={styles.video} />
        </div>
      );
    }
    if (block.type === 'image') {
      return (
        <div key={index} style={styles.imageContainer}>
          <img src={block.url} alt={block.alt || ''} style={styles.image} />
        </div>
      );
    }
    if (block.type === 'file') {
      return (
        <div key={index} style={styles.fileBlock}>
          <span style={styles.fileIcon}>📎</span>
          <a href={block.url} target="_blank" rel="noopener noreferrer" style={styles.fileLink}>
            {block.name || 'Download File'}
          </a>
        </div>
      );
    }
    if (block.type === 'hyperlink') {
      return (
        <div key={index} style={styles.linkBlock}>
          <a href={block.url} target="_blank" rel="noopener noreferrer" style={styles.hyperlink}>
            🔗 {block.label || block.url}
          </a>
        </div>
      );
    }
    return null;
  };

  const renderContent = (content) => {
    if (!content || !content.blocks || content.blocks.length === 0) {
      return (
        <div style={styles.emptyContent}>
          <p style={styles.emptyText}>
            Content coming soon. Check back after your instructor has added materials for this task.
          </p>
        </div>
      );
    }
    return content.blocks.map((block, index) => renderBlock(block, index));
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button style={styles.returnBtn} onClick={() => navigate(-1)}>
            ← Back to Module
          </button>
        </div>
      </div>

      <div style={styles.main}>
        {loading && (
          <div style={styles.centered}>
            <p style={styles.loadingText}>Loading task...</p>
          </div>
        )}

        {!loading && task && (
          <div>
            <div style={styles.taskHeader}>
              <span style={styles.taskTypeBadge}>{taskTypeLabel(task.task_type)}</span>
              <h2 style={styles.taskTitle}>{task.title}</h2>
              {completed && (
                <div style={styles.completedBadge}>✓ Completed</div>
              )}
            </div>

            <div style={styles.contentArea}>
              {renderContent(task.content)}
            </div>

            {!completed && (
              <div style={styles.completeSection}>
                <button
                  style={completing ? styles.completeBtnDisabled : styles.completeBtn}
                  onClick={handleComplete}
                  disabled={completing}
                >
                  {completing ? 'Marking Complete...' : '✓ Mark as Complete'}
                </button>
              </div>
            )}

            {completed && (
              <div style={styles.completedSection}>
                <div style={styles.completedCard}>
                  <p style={styles.completedText}>✓ You have completed this task</p>
                  <button style={styles.backBtn} onClick={() => navigate(-1)}>
                    ← Return to Module
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  welcome: { color: '#ffffff', fontSize: '14px' },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: { maxWidth: '800px', margin: '0 auto', padding: '32px' },
  centered: { display: 'flex', justifyContent: 'center', padding: '60px' },
  loadingText: { color: '#ffffff60', fontSize: '16px' },
  taskHeader: { marginBottom: '32px' },
  taskTypeBadge: {
    backgroundColor: '#1A6B8A20',
    color: '#1A6B8A',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
    marginBottom: '12px',
  },
  taskTitle: { color: '#ffffff', fontSize: '28px', fontWeight: '700', margin: '0 0 12px 0' },
  completedBadge: {
    backgroundColor: '#1E844920',
    color: '#1E8449',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    display: 'inline-block',
  },
  contentArea: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '12px',
    padding: '32px',
    marginBottom: '32px',
    minHeight: '300px',
  },
  emptyContent: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' },
  emptyText: { color: '#ffffff40', fontSize: '15px', textAlign: 'center', lineHeight: '1.6', maxWidth: '400px' },
  contentHeading: { color: '#C49A28', fontSize: '22px', fontWeight: '700', margin: '0 0 16px 0' },
  contentText: { color: '#ffffffcc', fontSize: '16px', lineHeight: '1.8', margin: '0 0 16px 0' },
  videoContainer: { width: '100%', aspectRatio: '16/9', marginBottom: '24px', borderRadius: '8px', overflow: 'hidden' },
  video: { width: '100%', height: '100%', border: 'none' },
  imageContainer: { marginBottom: '24px' },
  image: { width: '100%', borderRadius: '8px' },
  fileBlock: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff15',
    borderRadius: '8px',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  fileIcon: { fontSize: '18px' },
  fileLink: { color: '#1A6B8A', fontSize: '14px', textDecoration: 'none', fontWeight: '600' },
  linkBlock: { marginBottom: '12px' },
  hyperlink: { color: '#1A6B8A', fontSize: '15px', textDecoration: 'none', fontWeight: '600' },
  completeSection: { display: 'flex', justifyContent: 'center', marginTop: '16px' },
  completeBtn: {
    backgroundColor: '#1E8449',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '16px 48px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '0.5px',
  },
  completeBtnDisabled: {
    backgroundColor: '#1E844960',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '16px 48px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'not-allowed',
    letterSpacing: '0.5px',
  },
  completedSection: { marginTop: '16px' },
  completedCard: {
    backgroundColor: '#1E844915',
    border: '1px solid #1E844940',
    borderRadius: '10px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  completedText: { color: '#1E8449', fontSize: '16px', fontWeight: '600', margin: '0' },
  backBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #1E8449',
    color: '#1E8449',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default TaskView;